import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { AutocompleteOption } from "../../app/shared/lib/ni-autocomplete/models/ni-autocomplete.model";
import { AutocompleteSelectEvent } from "../../app/shared/lib/ni-autocomplete/ni-autocomplete.component";
import { ActivatedRoute, Router } from "@angular/router";
import { UserService } from "../core/services/user.service";
import { LanguageService } from "../core/services/language.service";
import { Subject, of, Subscription } from "rxjs";
import { debounceTime, takeUntil, switchMap, tap } from "rxjs/operators";
import { DateUtils } from "../shared/utils/date-utils";
import { pageLoadAnimation } from "../shared/utils/common-animations";
import { getProgramId, getSelectedLocation } from "../shared/utils/storage-utils";

@Component({
  selector: "card-checkin-component, CardCheckinComponent",
  standalone: true,
  templateUrl: './card-checkin.component.html',
  styleUrls: ['./card-checkin.component.css'],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimation]
})
export class CardCheckinComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly languageService = inject(LanguageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private languageSub?: Subscription;

  userList: any[] = [];
  isLoadingUsers = false;
  userListError: string | null = null;
  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private currentSearchText: string = ''; // Track current search to prevent duplicate loading
  private searchCounter: number = 0; // Counter to force distinct values after clearing
  submitting: boolean = false;
  toasterMessage: string | null = null;
  toasterVariant: "success" | "danger" | "warning" | "info" = "info";
  private toasterTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly userService: UserService
  ) {
    // Setup debounced search with request cancellation
    this.searchSubject.pipe(
      // Set loading state immediately when search text is emitted (before debounce)
      // Only set loading if it's a new search text
      
      tap((searchText: string) => {
        const trimmedText = searchText.trim();
        // Only set loading if it's a different search and meets minimum length
        if (trimmedText !== this.currentSearchText && trimmedText.length >= 3) {
          this.isLoadingUsers = true;
          this.userListError = null;
        }
      }),
         
      debounceTime(500), // Debounce for 500ms
    
      switchMap((searchText: string) => {
        // Handle clear marker - don't make API call, just reset state
        if (searchText.startsWith('__CLEAR__')) {
          this.isLoadingUsers = false;
          this.currentSearchText = '';
          return of(null);
        }
        
        // Check if search text meets minimum requirements
        const trimmedText = searchText.trim();
        if (trimmedText.length < 3 || this.programId === null) {
          // Clear loading state if search doesn't meet requirements
          this.isLoadingUsers = false;
          this.currentSearchText = '';
          // Return an observable that completes immediately to clear loading
          return of(null);
        }
        
        // Only make API call if this is a different search than what's currently being processed
        // Compare trimmed text to avoid duplicate calls for same meaningful content
        if (trimmedText === this.currentSearchText) {
          // Same search is already in progress, don't make duplicate call
          return of(null);
        }
        
        // Track current search before making API call (use trimmed for comparison)
        this.currentSearchText = trimmedText;
        
        // Cancel previous request and start new one
        // Preserve original searchText with spaces for the API call
        const payload = {
          programId: this.programId,
          index: 0,
          limit: 10,
          searchText: searchText // Preserve spaces in search text
        };
        return this.userService.getUserList(payload);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // Handle null response (when search doesn't meet requirements)
        if (response === null) {
          this.isLoadingUsers = false;
          this.currentSearchText = '';
          return;
        }
        
        this.isLoadingUsers = false;
        this.currentSearchText = ''; // Reset tracking after successful response
        // Handle different response structures
        let users: any[] = [];
        if (Array.isArray(response)) {
          users = response;
        } else if (response?.data && Array.isArray(response.data)) {
          users = response.data;
        } else if (response?.users && Array.isArray(response.users)) {
          users = response.users;
        }
        
        // Transform user data to AutocompleteOption format
        this.names = this.transformUsersToOptions(users);
        this.userList = users;
      },
      error: (error) => {
        this.isLoadingUsers = false;
        this.currentSearchText = ''; // Reset tracking after error
        this.userListError = error?.error?.message ?? 'Failed to fetch user list';
        this.names = [];
      }
    });
  }
  pageLoadState = 'hidden';
  selectedName: AutocompleteOption | null = null;
  selectedDate: Date | null = null;
  maxDate: Date = new Date();
  readonly defaultDobViewDate: Date = this.getDefaultDobViewDate();
  names: AutocompleteOption[] = [];
  programId: number | null = null;
  animatedPlaceholder: string = '';
  private placeholderAnimationInterval: ReturnType<typeof setInterval> | null = null;
  onNameSelect(event: AutocompleteSelectEvent): void {
    this.selectedName = event?.value ?? null;
    // Reset the DOB field whenever the participant selection changes
    this.selectedDate = null;
  }

  onDateChange(date: Date | null): void {
    this.selectedDate = date;
  }

  /** Working center from staff location selection (never a hardcoded center name). */
  get centerDisplayName(): string {
    const name = getSelectedLocation()?.locationName?.trim();
    return name || 'Senior Center';
  }

  goBack(): void {
    void this.router.navigate(['/programs/senior-center']);
  }

  onVerify(): void {
    if (this.selectedName && this.selectedDate) {
      this.submitting = true;
      const payload = {
        userId: this.selectedName?.value,
        dob: this.formatDob(this.selectedDate)
      };
      this.userService.verifyUser(payload).subscribe({
        next: (response) => {
          this.submitting = false;
          if (this.selectedName?.value) {
           sessionStorage.setItem('userDetails', JSON.stringify(this.selectedName));
          }
          this.router.navigate(['/success-meal-transport']);
        },
        error: (error) => {
          this.submitting = false;
          const message =
            error?.error?.message ??
            error?.message ??
            'Unable to verify your account right now. Please try again.';
          this.showToaster(message, 'danger');
        }
      });
      // Add your verification logic here
    }
  }

  onRegister(): void {
    // Add your registration navigation logic here
    this.router.navigate(['/register']);
  }

  handleVerifyKeyDown(event: KeyboardEvent): void {
    // Handle Enter or Space key to trigger verify action (accessibility)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onVerify();
    }
  }

  handleRegisterKeyDown(event: KeyboardEvent): void {
    // Handle Enter or Space key to trigger register action (accessibility)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRegister();
    }
  }

  handleCardDivKeyDown(event: KeyboardEvent): void {
    // Handle keyboard events on the card check-in div (accessibility)
    // This allows keyboard users to interact with the visual element
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // Focus on the autocomplete input when Enter/Space is pressed
      const autocompleteInput = document.querySelector('NiAutocomplete input');
      if (autocompleteInput instanceof HTMLElement) {
        autocompleteInput.focus();
      }
    }
  }

  get verifyButtonLabel(): string {
    return this.submitting
      ? this.languageService.t('cardCheckin.verifying')
      : this.languageService.t('cardCheckin.verify');
  }

  get searchHint(): string {
    return this.languageService.t('cardCheckin.hint.search');
  }

  get dobHint(): string {
    return this.languageService.t('cardCheckin.hint.dob');
  }

  get searchPlaceholderText(): string {
    return this.languageService.t('cardCheckin.placeholder.search');
  }

  get dobPlaceholderText(): string {
    return this.languageService.t('cardCheckin.placeholder.dob');
  }

  get tapCardText(): string {
    return this.languageService.t('cardCheckin.tapCard');
  }

  private showToaster(
    message: string,
    variant: "success" | "danger" | "warning" | "info" = "info",
    duration = 4000
  ): void {
    this.toasterMessage = message;
    this.toasterVariant = variant;
    if (this.toasterTimeoutId) {
      clearTimeout(this.toasterTimeoutId);
    }
    this.toasterTimeoutId = setTimeout(() => {
      this.dismissToaster();
    }, duration);
  }

  dismissToaster(): void {
    this.toasterMessage = null;
    if (this.toasterTimeoutId) {
      clearTimeout(this.toasterTimeoutId);
      this.toasterTimeoutId = null;
    }
  }

  ngOnInit(): void {
    this.languageSub = this.languageService.language$.subscribe(() => this.cdr.markForCheck());
    // Capture the program id from the route so downstream calls stay scoped correctly
    this.programId = getProgramId();
    this.consumeFlashToastFromNavigationState();
    // Don't fetch user list on initial load - wait for user to type at least 3 characters
  }

  private consumeFlashToastFromNavigationState(): void {
    const state = history.state as {
      flashToast?: { message?: string; variant?: 'success' | 'danger' | 'warning' | 'info'; duration?: number };
      [key: string]: any;
    };
    const flash = state?.flashToast;
    if (!flash?.message) {
      return;
    }

    this.showToaster(
      flash.message,
      flash.variant || 'info',
      typeof flash.duration === 'number' ? flash.duration : 3000
    );

    // Clear flash data so it doesn't replay on refresh/back.
    const nextState = { ...state };
    delete nextState.flashToast;
    window.history.replaceState(nextState, '');
  }

  /**
   * Triggers a debounced search for users
   * All API calls go through the debounced subject to prevent excessive requests
   * @param searchText Optional search text to filter users
   */
  fetchUserList(searchText: string = ''): void {
    // Emit search text to the debounced subject
    // The actual API call will be handled by the debounced subscription in constructor
    this.searchSubject.next(searchText);
  }

  /**
   * Transforms user data from API to AutocompleteOption format
   * @param users Array of user objects from API
   * @returns Array of AutocompleteOption
   */
  private transformUsersToOptions(users: any[]): AutocompleteOption[] {
    return users.map((user) => {
      // Extract user information - adjust field names based on your API response
      const userId = user.id ?? user.userId ?? user._id ?? user.value ?? '';
      
      // Handle name - try different possible field names
      let userName = '';
      if (user.name) {
        userName = user.name;
      } else if (user.fullName) {
        userName = user.fullName;
      } else if (user.firstName || user.lastName) {
        const firstName = user.firstName ?? '';
        const lastName = user.lastName ?? '';
        userName = `${firstName} ${lastName}`.trim();
      } else if (user.label) {
        userName = user.label;
      }
      
      // Handle phone number - format when only digits are provided so UI shows (+cc) local-number
      let userPhone = user.phone ?? user.mobile ?? user.phoneNumber ?? user.subtext ?? '';
      userPhone = this.formatPhoneNumber(userPhone);
      return {
        value: userId,
        label: userName,
        subtext: userPhone
      };
    });
  }

  /**
   * Formats raw phone numbers into (+country) localNumber when possible.
   * Keeps any pre-formatted strings intact.
   */
  private formatPhoneNumber(phone: any): string {
    if (phone == null) {
      return '';
    }

    const value = String(phone).trim();
    if (!value) {
      return '';
    }

    // If the phone already contains formatting characters, keep it as is
    if (/[()\-\s]/.test(value) && value.includes('+')) {
      return value;
    }

    const startsWithPlus = value.startsWith('+');
    const digitsOnly = value.replace(/\D/g, '');

    if (!digitsOnly) {
      return value;
    }

    // If we have a leading + but only 10 (or fewer) digits, keep original
    if (startsWithPlus && digitsOnly.length <= 10) {
      return value;
    }

    if (startsWithPlus) {
      const localLength = Math.min(10, digitsOnly.length);
      const countryLength = Math.max(0, digitsOnly.length - localLength);
      const countryCode = digitsOnly.slice(0, countryLength);
      const localNumber = digitsOnly.slice(countryLength);

      if (countryCode) {
        return `(+${countryCode}) ${localNumber}`;
      }
    }

    return value;
  }

  private formatDob(date: Date | null): string | null {
    if (!date) {
      return null;
    }
    return DateUtils.formatDate(date, 'YYYY-MM-DD');
  }

  /**
   * Handles raw input events from the autocomplete component
   * @param event Input event emitted by NiAutocomplete
   */
  onAutocompleteInput(event: Event): void {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    const searchText = (input?.value ?? '').trim();

    if (searchText.trim().length >= 3) {
      // Use fetchUserList which now handles debouncing internally
      this.fetchUserList(searchText);
      return;
    }

    // Clear the dropdown when fewer than 3 characters are present
    this.names = [];
    this.userList = [];
    this.isLoadingUsers = false;
    this.currentSearchText = ''; // Reset tracking when clearing
    // Increment counter to force distinct values after clearing
    this.searchCounter++;
    // Emit a unique value to reset distinctUntilChanged state
    this.searchSubject.next(`__CLEAR__${this.searchCounter}__`);
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.dismissToaster();
    // Clear placeholder animation interval
    if (this.placeholderAnimationInterval) {
      clearInterval(this.placeholderAnimationInterval);
      this.placeholderAnimationInterval = null;
    }
  }

  ngAfterViewInit(): void {
    // Trigger animation immediately after view init
    // Use a small delay to ensure Angular has processed the initial state
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
      }, 50);
    });
    // Start placeholder animation
    this.startPlaceholderAnimation();
  }

  /**
   * Starts the typewriter animation for the placeholder text
   * Types out the text letter by letter with dots looping continuously, then repeats
   */
  private startPlaceholderAnimation(): void {
    const fullText = this.searchPlaceholderText;
    const maxDots = 3;
    const typingSpeed = 100; // ms per letter
    const dotSpeed = 500; // ms per dot change
    const dotHoldTime = 1000; // ms to hold when dots reach maximum
    const maxDotLoops = 2; // Number of times to loop dots after typing completes before restarting

    const animationState = {
      currentIndex: 0,
      dotCount: 0,
      lastTypingUpdate: Date.now(),
      lastDotUpdate: Date.now(),
      dotLoopCount: 0,
      isHoldingDots: false,
      dotHoldStartTime: 0
    };

    const animate = () => {
      const now = Date.now();
      
      if (animationState.currentIndex < fullText.length) {
        this.handleTypingPhase(now, fullText, animationState, typingSpeed);
        return;
      }
      
      this.handleDotAnimationPhase(now, fullText, animationState, maxDots, dotSpeed, dotHoldTime, maxDotLoops);
    };

    // Use a single interval for both phases
    this.placeholderAnimationInterval = setInterval(animate, 50); // Check every 50ms for smooth animation
  }

  /**
   * Handles the typing phase of the animation
   */
  private handleTypingPhase(
    now: number,
    fullText: string,
    state: { currentIndex: number; lastTypingUpdate: number },
    typingSpeed: number
  ): void {
    const timeSinceTypingUpdate = now - state.lastTypingUpdate;
    if (timeSinceTypingUpdate >= typingSpeed) {
      state.currentIndex++;
      state.lastTypingUpdate = now;
    }
    // Update placeholder with current text only (no dots while typing)
    this.animatedPlaceholder = fullText.substring(0, state.currentIndex);
  }

  /**
   * Handles the dot animation phase after typing is complete
   */
  private handleDotAnimationPhase(
    now: number,
    fullText: string,
    state: {
      dotCount: number;
      lastDotUpdate: number;
      isHoldingDots: boolean;
      dotHoldStartTime: number;
      dotLoopCount: number;
      currentIndex: number;
      lastTypingUpdate: number;
    },
    maxDots: number,
    dotSpeed: number,
    dotHoldTime: number,
    maxDotLoops: number
  ): void {
    if (!state.isHoldingDots) {
      this.updateDotsWhenNotHolding(now, state, maxDots, dotSpeed);
    } else {
      this.updateDotsWhenHolding(now, state, maxDots, dotSpeed, dotHoldTime);
    }
    
    // Show text with dots after typing is complete
    const dots = '.'.repeat(state.dotCount);
    this.animatedPlaceholder = fullText + dots;
    
    // After looping dots a few times, reset and start typing again
    if (this.shouldResetAnimation(state, maxDotLoops)) {
      this.resetAnimation(now, state);
    }
  }

  /**
   * Updates dots when not in holding state
   */
  private updateDotsWhenNotHolding(
    now: number,
    state: {
      dotCount: number;
      lastDotUpdate: number;
      isHoldingDots: boolean;
      dotHoldStartTime: number;
      dotLoopCount: number;
    },
    maxDots: number,
    dotSpeed: number
  ): void {
    const timeSinceDotUpdate = now - state.lastDotUpdate;
    if (timeSinceDotUpdate < dotSpeed) {
      return;
    }

    state.dotCount = (state.dotCount + 1) % (maxDots + 1);
    state.lastDotUpdate = now;
    
    // When dots reach maximum, hold/pause for a moment
    if (state.dotCount === maxDots) {
      state.isHoldingDots = true;
      state.dotHoldStartTime = now;
    }
    
    // Track dot loops when we complete a cycle (0 -> 1 -> 2 -> 3 -> 0)
    if (state.dotCount === 0) {
      state.dotLoopCount++;
    }
  }

  /**
   * Updates dots when in holding state
   */
  private updateDotsWhenHolding(
    now: number,
    state: {
      dotCount: number;
      lastDotUpdate: number;
      isHoldingDots: boolean;
      dotHoldStartTime: number;
      dotLoopCount: number;
    },
    maxDots: number,
    dotSpeed: number,
    dotHoldTime: number
  ): void {
    const timeSinceHoldStart = now - state.dotHoldStartTime;
    if (timeSinceHoldStart < dotHoldTime) {
      return;
    }

    state.isHoldingDots = false;
    // Continue to next dot count (will reset to 0)
    state.dotCount = (state.dotCount + 1) % (maxDots + 1);
    state.lastDotUpdate = now;
    
    // Track dot loops when we complete a cycle
    if (state.dotCount === 0) {
      state.dotLoopCount++;
    }
  }

  /**
   * Checks if animation should be reset
   */
  private shouldResetAnimation(
    state: { dotLoopCount: number; dotCount: number; isHoldingDots: boolean },
    maxDotLoops: number
  ): boolean {
    return state.dotLoopCount >= maxDotLoops && state.dotCount === 0 && !state.isHoldingDots;
  }

  /**
   * Resets the animation to start the cycle over
   */
  private resetAnimation(
    now: number,
    state: {
      currentIndex: number;
      dotCount: number;
      dotLoopCount: number;
      isHoldingDots: boolean;
      lastTypingUpdate: number;
      lastDotUpdate: number;
    }
  ): void {
    state.currentIndex = 0;
    state.dotCount = 0;
    state.dotLoopCount = 0;
    state.isHoldingDots = false;
    this.animatedPlaceholder = '';
    state.lastTypingUpdate = now;
    state.lastDotUpdate = now;
  }

  private getDefaultDobViewDate(): Date {
    const today = new Date();
    return new Date(today.getFullYear() - 40, today.getMonth(), today.getDate());
  }

}
